/* ────────────────────────────────────────────────────────────────────────────
 * GENERATION PROVIDER — CONTRACT
 * ────────────────────────────────────────────────────────────────────────────
 * This file is the ONLY thing the UI knows about video generation.
 *
 * The UI never talks to an engine directly. It talks to a `GenerationProvider`.
 * Today that provider is `mockProvider` (simulated). Tomorrow it will be
 * `maestroProvider` (the real local Pinokio/Maestro HTTP API).
 *
 * Swapping engines = editing ONE line in `src/generation/index.ts`.
 * Do not import a concrete provider anywhere else in the app.
 * ──────────────────────────────────────────────────────────────────────────── */

export type GenerationMode = 'text-to-video' | 'image-to-video';

export type Resolution = '480p' | '720p' | '1080p';

/** Frame orientation. Portrait is the vertical 9:16 social format. */
export type AspectRatio = 'landscape' | 'portrait';

/** Engine-facing settings. Chosen in the Settings panel. */
export interface GenerationSettings {
  resolution: Resolution;
  aspectRatio: AspectRatio;
  /** Diffusion/sampling steps. Higher = slower, (in theory) better. */
  steps: number;
  /** Whether the engine may reuse its cached latents/models between runs. */
  cacheEnabled: boolean;
  /**
   * Which model to bill and render with. Ids come from the catalogue in
   * `src/lib/pricing.ts`; each one carries its own per-second rate.
   */
  modelId: import('../lib/pricing').ModelId;
}

/** Everything needed to start one generation. */
export interface GenerationRequest {
  mode: GenerationMode;
  prompt: string;
  /**
   * Only present in `image-to-video` mode.
   * A blob/object URL pointing at the user's uploaded still.
   * A real provider will need to re-read this as bytes before POSTing.
   */
  imageDataUrl?: string;
  /** Original filename of the uploaded still, if any. */
  imageName?: string;
  settings: GenerationSettings;
  /** Deterministic seed. Same seed + same prompt should give the same clip. */
  seed: number;
}

/** Coarse lifecycle stage — drives the UI state machine. */
export type GenerationStage =
  | 'idle'
  | 'queued'
  | 'generating'
  | 'finalizing'
  | 'done'
  | 'error'
  | 'cancelled';

/** Streamed back to the UI many times per second while a job runs. */
export interface GenerationProgress {
  stage: GenerationStage;
  /** 0..1 */
  progress: number;
  /** Seconds remaining, best effort. `null` when unknown. */
  etaSeconds: number | null;
  /** Short human-readable status line, e.g. "sampling step 14/30". */
  message: string;
  /** Optional: current sampling step, when the engine reports it. */
  step?: number;
  totalSteps?: number;
}

/** The finished artefact. */
export interface GenerationResult {
  /** Playable URL (object URL, blob URL, file:// or http:// — the UI does not care). */
  videoUrl: string;
  /** Still frame for poster/thumbnail use. Data URL or http URL. */
  posterUrl: string;
  /** Container/codec of `videoUrl`, e.g. "video/webm" or "video/mp4". */
  mimeType: string;
  /** Suggested download filename, extension included. */
  filename: string;
  durationSeconds: number;
  width: number;
  height: number;
  /**
   * True when the provider could not produce a moving image and `videoUrl`
   * is a still fallback. The UI shows an honest notice in this case.
   */
  isStillFallback?: boolean;
}

export interface StartJobOptions {
  request: GenerationRequest;
  /** Called frequently with progress updates. */
  onProgress: (progress: GenerationProgress) => void;
  /** Aborts the job. Providers must honour this. */
  signal: AbortSignal;
}

export interface GenerationProvider {
  /** Stable id, e.g. "mock" | "maestro". Shown in the UI status pill. */
  readonly id: string;
  /** Human label for the UI, e.g. "Mock motor". */
  readonly label: string;
  /** False for the mock — lets the UI show the "engine offline" badge. */
  readonly isLive: boolean;

  /**
   * Cheap liveness probe. The UI calls this on mount and shows the result in
   * the status pill. Must never throw — resolve `false` instead.
   */
  healthCheck(): Promise<boolean>;

  /**
   * Runs one generation to completion.
   * Must reject with a `DOMException` named "AbortError" when aborted.
   */
  generate(options: StartJobOptions): Promise<GenerationResult>;

  /**
   * Rough wall-clock estimate for a run with these settings, in seconds.
   * Shown next to the Generate button *before* the job starts, so it only
   * has to be in the right ballpark. Optional — the UI hides the estimate
   * when a provider cannot give one.
   */
  estimateSeconds?(settings: GenerationSettings): number;

  /**
   * Release anything the provider handed out for a finished result
   * (e.g. revoke object URLs). Optional.
   */
  releaseResult?(result: GenerationResult): void;

  /**
   * Re-fetches a playable URL for a clip from a PAST session, by the
   * `filename` stored in history. History never persists `videoUrl` (it's a
   * runtime-only blob URL that dies with the page), so after a reload this is
   * the only way to play/download an old clip again. Providers that keep
   * their output on disk (like Maestro) can serve it back up; providers that
   * don't (like the mock, which never writes anywhere) simply omit this.
   * Resolve `null` — never throw — when the file is unavailable (engine
   * offline, deleted, etc.); the UI shows that as a plain "not available".
   */
  resolveHistoryUrl?(filename: string): Promise<string | null>;
}

/** Thrown by providers for expected, user-presentable failures. */
export class GenerationError extends Error {
  constructor(
    message: string,
    public readonly cause?: unknown,
  ) {
    super(message);
    this.name = 'GenerationError';
  }
}
